/**
 * A cron expression read back in words.
 *
 * A port of `cronInWords` in shared/src/commonMain/.../api/Schedule.kt, kept because the task form
 * says what an expression means as it is typed and a round trip per keystroke to be told
 * "every 6 hours" is not worth making.
 *
 * The authority on what an expression actually does is `Cron` on the server. This reads the shapes
 * people type and says nothing about the ones it does not know. An expression it cannot read, or one
 * holding a number out of range, returns null and the screen shows only the expression, which is
 * still true. The one thing it must never do is describe an expression as something other than what
 * the server will run — which is why its tests are the same cases as CronWordsTest.kt.
 */

const FIELDS = 5;
const ANY = "*";

const MINUTES: [number, number] = [0, 59];
const HOURS: [number, number] = [0, 23];
const DAYS_OF_MONTH: [number, number] = [1, 31];
const DAYS_OF_WEEK: [number, number] = [0, 7];

/** The last day a span can name before it has wrapped around to being every day. */
const SATURDAY = 6;
/** Sunday counts twice in the names but only once in the week. */
const DAYS_IN_WEEK = 7;

/** Sunday twice over, because both 0 and 7 mean it and both are typed. */
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

interface Fields {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}

/** Whether neither day field narrows anything, which is what lets a reading say "every day". */
function everyDay(read: Fields): boolean {
  return read.dayOfMonth === ANY && read.dayOfWeek === ANY;
}

export function cronInWords(expression: string): string | null {
  const parts = expression.trim().split(/\s+/).filter((it) => it.length > 0);
  if (parts.length !== FIELDS) return null;

  const read: Fields = {
    minute: parts[0]!,
    hour: parts[1]!,
    dayOfMonth: parts[2]!,
    month: parts[3]!,
    dayOfWeek: parts[4]!,
  };

  // Months are left unread: a schedule that fires in March is rare enough that saying nothing is
  // better than a sentence nobody has checked.
  if (read.month !== ANY) return null;

  return oftenerThanADay(read) ?? onceADay(read);
}

/** The readings that repeat within a day: every minute, every so many minutes, every so many hours. */
function oftenerThanADay(read: Fields): string | null {
  if (!everyDay(read)) return null;
  if (read.hour === ANY) return withinTheHour(read.minute);

  const step = stepOf(read.hour, HOURS);
  if (step === null) return null;

  const minute = numberIn(read.minute, MINUTES);
  if (minute === null) return null;

  return `Every ${many(step, "hour")}` + (minute === 0 ? "" : ` at ${past(minute)}`);
}

function withinTheHour(minute: string): string | null {
  if (minute === ANY) return "Every minute";

  const step = stepOf(minute, MINUTES);
  if (step !== null) return `Every ${many(step, "minute")}`;

  const at = numberIn(minute, MINUTES);
  return at === null ? null : `Every hour at ${past(at)}`;
}

/** The readings that fire at one time of day: every day, on named weekdays, or on a date. */
function onceADay(read: Fields): string | null {
  const minute = numberIn(read.minute, MINUTES);
  if (minute === null) return null;

  const hour = numberIn(read.hour, HOURS);
  if (hour === null) return null;

  const time = `${two(hour)}:${two(minute)}`;

  if (everyDay(read)) return `Every day at ${time}`;
  if (read.dayOfMonth === ANY) {
    const days = weekdays(read.dayOfWeek);
    return days === null ? null : `${days} at ${time}`;
  }
  if (read.dayOfWeek === ANY) {
    const day = dayOfMonth(read.dayOfMonth);
    return day === null ? null : `${day} at ${time}`;
  }
  return null;
}

/** "Every Sunday", "Every Monday to Friday", "Every Monday, Wednesday and Friday". */
function weekdays(field: string): string | null {
  return field.includes("-") ? weekdaySpan(field) : weekdayList(field);
}

function weekdaySpan(field: string): string | null {
  const ends = field.split("-");
  if (ends.length !== 2) return null;

  const from = numberIn(ends[0]!, DAYS_OF_WEEK);
  const to = numberIn(ends[1]!, DAYS_OF_WEEK);
  if (from === null || to === null) return null;

  // A span covering the whole week is every day, and reading it as Sunday to Sunday would be worse
  // than useless. Anything running past Saturday only makes sense as that.
  if (from === 0 && to >= SATURDAY) return "Every day";

  return from < to && to <= SATURDAY ? `Every ${WEEKDAYS[from]} to ${WEEKDAYS[to]}` : null;
}

function weekdayList(field: string): string | null {
  const days = field.split(",").map((it) => numberIn(it, DAYS_OF_WEEK));
  if (days.some((it) => it === null)) return null;

  // Sunday is both 0 and 7, so a list holding each of them names one day rather than two.
  const named = [...new Set(days.map((it) => it! % DAYS_IN_WEEK))]
    .sort((a, b) => a - b)
    .map((it) => WEEKDAYS[it]!);

  return `Every ${list(named)}`;
}

function dayOfMonth(field: string): string | null {
  const day = numberIn(field, DAYS_OF_MONTH);
  return day === null ? null : `The ${ordinal(day)} of every month`;
}

/** The N of `*` over N, when that is the whole field. */
function stepOf(field: string, range: [number, number]): number | null {
  const parts = field.split("/");
  if (parts.length !== 2 || parts[0] !== ANY) return null;

  const step = numeric(parts[1]!);
  if (step === null) return null;

  return step > 1 && step <= range[1] ? step : null;
}

function numberIn(field: string, range: [number, number]): number | null {
  const value = numeric(field);
  if (value === null) return null;
  return value >= range[0] && value <= range[1] ? value : null;
}

/** Kotlin's `toIntOrNull`: whole numbers only, and nothing else in the string. */
function numeric(text: string): number | null {
  if (!/^-?\d+$/.test(text)) return null;
  return Number.parseInt(text, 10);
}

/** "6 hours", but "hour" when there is one of them. */
function many(count: number, unit: string): string {
  return count === 1 ? unit : `${count} ${unit}s`;
}

/** A minute of the hour, on its own. */
function past(minute: number): string {
  return `:${two(minute)}`;
}

function two(value: number): string {
  return String(value).padStart(2, "0");
}

/** "Monday, Wednesday and Friday", the way it is said rather than the way it is stored. */
function list(items: string[]): string {
  if (items.length === 1) return items[0]!;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]!}`;
}

/** The teens are the reason this is not a lookup on the last digit. */
function ordinal(value: number): string {
  const teen = value % 100;
  if (teen >= 11 && teen <= 13) return `${value}th`;

  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}
